import { compressImageToJPEG } from '@/utils/mediaUtils';
import { ImageVariant } from '@/hooks/useMultiSizeImageProcessor';
import { debugLogger } from '@/utils/debugLogger';

/**
 * Gera as 4 variantes numéricas padrão para qualquer arquivo de imagem
 * 
 * Sistema v2 - 4 variantes numeradas (1:1 square):
 * - 0: 80x80px @ 85% quality (avatar, chat list)
 * - 1: 200x200px @ 75% quality (chat header, mini previews)
 * - 2: 500x500px @ 80% quality (feed/connection cards)
 * - 3: 1200x1200px @ 90% quality (profile zoom, máximo)
 * 
 * @param imageFile - Arquivo de imagem original ou já cortado (1:1)
 * @returns Array com 4 variantes no formato ImageVariant
 */
export const generatePhotoVariants = async (imageFile: File): Promise<ImageVariant[]> => {
  debugLogger.debug('[photoVariantGenerator] Generating v2 numeric variants (1:1) for:', imageFile.name);
  
  try {
    const variants = await Promise.all([
      compressImageToJPEG(imageFile, 80, 0.85),   // micro-avatar
      compressImageToJPEG(imageFile, 200, 0.75),  // small-thumb
      compressImageToJPEG(imageFile, 500, 0.80),  // medium-card
      compressImageToJPEG(imageFile, 1200, 0.90)  // large-display
    ]);

    const dimensionsMap = [80, 200, 500, 1200];
    const qualityMap = [0.85, 0.75, 0.80, 0.90];
    const typeMap = [0, 1, 2, 3] as const;

    const imageVariants = variants.map((file, index) => ({
      type: typeMap[index],
      file,
      width: dimensionsMap[index],
      height: dimensionsMap[index],
      quality: qualityMap[index],
      actualSize: file.size,
      dimensions: `${dimensionsMap[index]}x${dimensionsMap[index]}`
    }));

    debugLogger.debug('[photoVariantGenerator] Generated numeric variants:', {
      original: imageFile.name,
      variants: imageVariants.map(v => ({
        variant: v.type,
        sizeKB: Math.round(v.actualSize / 1024),
        dimensions: v.dimensions
      }))
    });

    return imageVariants;
  } catch (error) {
    debugLogger.error('[photoVariantGenerator] Error generating variants:', error);
    throw error;
  }
};
